import { EventManager } from './EventManager';

/**
 * BaseApp - Ultra-generic base class for any type of application
 * Contains only the most essential functionality that ANY app needs
 * This will be copied to Arkturian as the foundation
 */
export abstract class BaseApp {
    // Core event system - every app needs events
    public readonly eventManager: EventManager;
    
    // Basic app state
    protected _isInitialized: boolean = false;
    protected _isRunning: boolean = false;
    
    // File handling - most apps need to load/save
    protected _filePath: string = '';
    
    protected constructor() {
        // Initialize only the most essential components
        this.eventManager = new EventManager();
        
        // Setup basic event handlers
        this.setupEventHandlers();
    }
    
    /**
     * Initialize the application - to be extended by subclasses
     */
    public async initialize(): Promise<void> {
        if (this._isInitialized) {
            throw new Error('App is already initialized. Please dispose before re-initializing.');
        }
        
        this.eventManager.emit('app:initializing');
        
        try {
            // Call subclass initialization
            await this.onInitialize();
            
            this._isInitialized = true;
            this.eventManager.emit('app:initialized');
            
        } catch (error) {
            this.eventManager.emit('app:error', error);
            throw error;
        }
    }
    
    /**
     * Start the application
     */
    public start(): void {
        if (!this._isInitialized) {
            throw new Error('App must be initialized before starting');
        }
        
        if (this._isRunning) {
            console.warn('App is already running');
            return;
        }
        
        this._isRunning = true;
        this.eventManager.emit('app:started');
        this.onStart();
    }
    
    /**
     * Stop the application
     */
    public stop(): void {
        if (!this._isRunning) {
            return;
        }
        
        this._isRunning = false;
        this.eventManager.emit('app:stopped');
        this.onStop();
    }
    
    /**
     * Save current state to file
     */
    public async saveToFile(filePath?: string): Promise<void> {
        const targetPath = filePath || this._filePath;
        if (!targetPath) {
            throw new Error('No file path specified for saving');
        }
        
        const content = await this.exportContent();
        // TODO: Implement file writing based on platform (Node.js vs Browser)
        //console.log('Save to file:', targetPath, content);
        
        this.eventManager.emit('file:saved', { filePath: targetPath });
    }
    
    /**
     * Load content from file
     */
    public async loadFromFile(filePath: string): Promise<void> {
        // TODO: Implement file reading based on platform (Node.js vs Browser)
        //console.log('Load from file:', filePath);
        
        // For now, just set the file path
        this._filePath = filePath;
        this.eventManager.emit('file:loaded', { filePath });
    }
    
    /**
     * Set current file path
     */
    public setFilePath(filePath: string): void {
        this._filePath = filePath;
    }
    
    /**
     * Get current file path
     */
    public getFilePath(): string {
        return this._filePath;
    }
    
    /**
     * Check if app is initialized
     */
    public isInitialized(): boolean {
        return this._isInitialized;
    }
    
    /**
     * Check if app is running
     */
    public isRunning(): boolean {
        return this._isRunning;
    }
    
    /**
     * Dispose resources and cleanup
     */
    public dispose(): void {
        this.stop();
        this.eventManager.removeAllListeners();
        this._isInitialized = false;
        
        // Call subclass cleanup
        this.onDispose();
    }
    
    // Abstract methods that subclasses must implement
    
    /**
     * Abstract method for subclass-specific initialization
     */
    protected abstract onInitialize(): Promise<void>;
    
    /**
     * Abstract method for subclass-specific start logic
     */
    protected abstract onStart(): void;
    
    /**
     * Abstract method for subclass-specific stop logic
     */
    protected abstract onStop(): void;
    
    /**
     * Abstract method for loading content - implemented by subclasses
     */
    public abstract loadContent(content: any, filePath?: string): Promise<void>;
    
    /**
     * Abstract method for exporting content - implemented by subclasses
     */
    public abstract exportContent(): Promise<any>;
    
    /**
     * Abstract method for subclass-specific cleanup
     */
    protected abstract onDispose(): void;
    
    // Private methods
    private setupEventHandlers(): void {
        // Basic event handling - can be extended by subclasses
        this.eventManager.on('app:error', (error) => {
            console.error('App error:', error);
        });
    }
} 